import { getIn, useFormik } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import Select from 'react-select'
import { Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap'
import { PropertyOfferSettings } from '../../../../api/offers/types'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../../../common/constants'
import { offerModalFields } from '../../../../common/forms/generalRealEstate/fields'
import { propertyResponseFields } from '../../../../common/response/property'
import { hideModal, updateModal } from '../../../../store/modal'
import { normalizeDecimalFields } from '../../../../utils/general'
import { renderAreaUnit, renderCurrency } from '../../../../utils/property'
import AppPermissionsSwitcher from '../../../AppPermissionsSwitcher/AppPermissionsSwitcher'
import OfferImagesOrder from '../components/OfferImagesOrder'
import TogglableInput from '../components/TogglableInput'
import { OfferModalItem, OfferSettingsFormValues, OfferSettingsModalProps } from '../types'
import { formatItemDisplayLabel } from '../utils'

interface PermissionsProps {
    showMapsOffer: boolean
}

const OfferSettingsModal = (props: OfferSettingsModalProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { OFFER_SETTINGS_MODAL, OFFER_BASE_MODAL } = MODAL_TYPES
    const { isOpen, offerSettingsModal } = props
    const { items, offerSettingsGroup, selectedPropertyId } = offerSettingsModal

    const areaUnit = APPLICATION_SETTINGS.AREA_UNIT
    const currency = APPLICATION_SETTINGS.CURRENCY

    const currentPropertyId = selectedPropertyId || offerSettingsGroup?.[0]?.property_id
    const currentSettings = offerSettingsGroup?.find((settings) => settings.property_id === +currentPropertyId) || offerSettingsGroup?.[0]
    const currentItem = items?.find((item) => item.id === +currentPropertyId) || items?.[0]

    const createInitialValues = (settingsGroupObj: PropertyOfferSettings, propertyItem: OfferModalItem): OfferSettingsFormValues => {
        const normalizedFields = normalizeDecimalFields(propertyItem, [
            propertyResponseFields.PRICE,
            propertyResponseFields.PRICE_M2,
            propertyResponseFields.AREA,
        ]) as Record<string, string | number>

        return {
            property_id: settingsGroupObj.property_id,
            offer_photo_order: settingsGroupObj.offer_photo_order ?? '',
            offer_include_ground_plan: settingsGroupObj.offer_include_ground_plan ?? false,
            offer_include_watermark: settingsGroupObj.offer_include_watermark ?? false,
            offer_include_map: settingsGroupObj.offer_include_map ?? false,
            offer_exclude_header: settingsGroupObj.offer_exclude_header ?? false,
            offer_exclude_agent: settingsGroupObj.offer_exclude_agent ?? false,
            // price fields - show original values even if excluded
            offer_property_price: (normalizedFields[propertyResponseFields.PRICE] as string) ?? '',
            offer_property_area: (normalizedFields[propertyResponseFields.AREA] as string) ?? '',
            // checkbox state based on whether user included/excluded (null = excluded)
            offer_property_price_checked: settingsGroupObj.offer_property_price !== null,
            offer_property_area_checked: settingsGroupObj.offer_property_area !== null,
        }
    }

    const formik = useFormik<OfferSettingsFormValues>({
        enableReinitialize: true,
        initialValues:
            currentSettings && currentItem
                ? createInitialValues(currentSettings, currentItem)
                : {
                      property_id: '',
                      offer_photo_order: '',
                      offer_include_ground_plan: false,
                      offer_include_watermark: false,
                      offer_include_map: false,
                      offer_exclude_header: false,
                      offer_exclude_agent: false,
                      offer_property_price: '',
                      offer_property_area: '',
                      offer_property_price_checked: false,
                      offer_property_area_checked: false,
                  },
        onSubmit: (values) => {
            const {
                property_id,
                offer_photo_order,
                offer_include_ground_plan,
                offer_include_watermark,
                offer_include_map,
                offer_exclude_header,
                offer_exclude_agent,
                offer_property_price,
                offer_property_price_checked,
                offer_property_area,
                offer_property_area_checked,
            } = values

            const selectedProperty = {
                property_id,
                offer_photo_order,
                offer_include_ground_plan,
                offer_include_watermark,
                offer_include_map,
                offer_exclude_header,
                offer_exclude_agent,
                offer_property_price: offer_property_price_checked ? offer_property_price : null,
                offer_property_area: offer_property_area_checked ? offer_property_area : null,
            }

            const updatedOfferSettingsGroup = offerSettingsGroup.map((settings) =>
                settings.property_id === property_id ? selectedProperty : settings
            )

            handleClose(updatedOfferSettingsGroup)
        },
    })

    const checkboxFields = useMemo(
        () => [
            {
                name: offerModalFields.OFFER_INCLUDE_GROUND_PLAN,
                label: t('modal.offerPrintSettings.checkbox.groundPlan'),
            },
            {
                name: offerModalFields.OFFER_INCLUDE_WATERMARK,
                label: t('modal.offerPrintSettings.checkbox.watermark'),
            },
            {
                name: offerModalFields.OFFER_INCLUDE_MAP,
                label: t('modal.offerPrintSettings.checkbox.googleMap'),
                permission: 'GoogleMaps',
                module: 'properties', // OfferSettingsModal is only available for properties not on projects
            },
            {
                name: offerModalFields.OFFER_EXCLUDE_HEADER,
                label: t('modal.offerPrintSettings.checkbox.header'),
            },
            {
                name: offerModalFields.OFFER_EXCLUDE_AGENT,
                label: t('modal.offerPrintSettings.checkbox.agent'),
            },
        ],
        [t]
    )

    const toggableInputFields = useMemo(
        () => [
            {
                name: offerModalFields.OFFER_PROPERTY_PRICE,
                label: t('modal.offerPrintSettings.price', { currency: renderCurrency(currency) }),
            },
            {
                name: offerModalFields.OFFER_PROPERTY_AREA,
                label: t('modal.offerPrintSettings.area', { areaUnit: renderAreaUnit(areaUnit) }),
            },
        ],
        [t, currency, areaUnit]
    )

    const propertySelectOptions = useMemo(() => {
        if (!items) return []

        return items.map((item) => ({
            label: formatItemDisplayLabel(item, areaUnit, currency),
            value: item.id.toString(),
        }))
    }, [items, areaUnit, currency])

    const handlePropertySelect = (selectedValue: string) => {
        const payload = {
            modalType: OFFER_SETTINGS_MODAL,
            modalProps: {
                selectedPropertyId: selectedValue,
            },
        }

        dispatch(updateModal(payload))
    }

    const handleClose = (updatedOfferSettingsGroup?: PropertyOfferSettings[]) => {
        if (updatedOfferSettingsGroup) {
            const updateBaseModalPayload = {
                modalType: OFFER_BASE_MODAL,
                modalProps: {
                    offerSettingsGroup: updatedOfferSettingsGroup,
                },
            }

            dispatch(updateModal(updateBaseModalPayload))
        }

        dispatch(hideModal({ modalType: OFFER_SETTINGS_MODAL }))
    }

    return (
        <Modal isOpen={isOpen} centered>
            <ModalHeader toggle={() => handleClose()}>{t('modal.offerPrintSettings.title')}</ModalHeader>
            <ModalBody>
                <Row>
                    <Col sm={12} className="mb-4">
                        <div className="d-flex justify-content-between">
                            <Label for={offerModalFields.AGENT_ID} id={`${offerModalFields.PROPERTY_ID}Lbl`}>
                                {t('modal.offerPrintSettings.select.property.title')}
                            </Label>
                        </div>
                        <Select
                            onChange={(selectedOption) => {
                                if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption) {
                                    handlePropertySelect(selectedOption.value)
                                }
                            }}
                            options={propertySelectOptions}
                            name={offerModalFields.PROPERTY_ID}
                            id={offerModalFields.PROPERTY_ID}
                            value={propertySelectOptions?.find((option) => option.value === formik.values.property_id?.toString()) ?? ''}
                            noOptionsMessage={() => t('form.common.select.noData')}
                            placeholder=""
                        />
                    </Col>
                </Row>
                <OfferImagesOrder formik={formik} />
                <Row>
                    {checkboxFields.map((field) => {
                        const CheckboxInput = (
                            <Col sm={12} className="mb-4 d-flex align-items-center" key={field.name}>
                                <div className="form-check">
                                    <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={field.name}
                                        name={field.name}
                                        onChange={formik.handleChange}
                                        checked={getIn(formik.values, field.name)}
                                    />
                                    <Label className="form-check-label" for={field.name}>
                                        {field.label}
                                    </Label>
                                </div>
                            </Col>
                        )

                        if (field.permission) {
                            return (
                                <AppPermissionsSwitcher
                                    key={field.name}
                                    permission={field.permission}
                                    module={field.module}
                                    selectedProperty={items.find((item) => item.id.toString() === formik.values.property_id?.toString())}
                                >
                                    {({ showMapsOffer }: PermissionsProps) => {
                                        return showMapsOffer ? CheckboxInput : null
                                    }}
                                </AppPermissionsSwitcher>
                            )
                        }

                        return CheckboxInput
                    })}
                </Row>
                {toggableInputFields.map((field) => (
                    <TogglableInput key={field.name + formik.values.property_id} formik={formik} name={field.name}>
                        {field.label}
                    </TogglableInput>
                ))}
                <div className="hstack gap-2 justify-content-end">
                    <Button type="submit" color="send" className="btn-label" onClick={() => formik.handleSubmit()}>
                        <i className="mdi mdi-check align-bottom label-icon align-middle fs-16 me-2"></i>
                        {t('button.save')}
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default OfferSettingsModal
